// Pre-filling the booking form from a CRM link session (MS-6621, T4.7).
//
// MS-6620 got the salesperson here: book-api verified the arrival, stored what it carried, and the
// SPA holds nothing but an opaque session id. This module turns the session that id names into
// form values, and records where each value came from so T6.2 can tag it.
//
// Three arrivals share one shape. An OutPerform signed link, a legacy Salesforce `book_*` URL and
// an MHub CRM `opportunityID` all become the same `prefill`, so nothing here asks which CRM it is
// looking at — the provider name is data to display, never a branch.
//
// The legacy Salesforce path is the one with history. It used to write a one-day cookie of the
// `book_*` parameters and read it back on the form; the buyer's name, phone and email therefore
// sat in the URL, in browser history, and in a cookie that outlived the tab. Now those parameters
// are posted once to `POST /crm/links/` and the answer is an id. That is the whole point of the
// change, so `legacyLinkParams` deliberately hands back the parameters to POST and nothing that
// could tempt a caller to keep them.
//
// Logic lives here rather than in Form.jsx because jest in this repository transforms plain JS but
// not JSX: anything in a component cannot be tested at all.

// Legacy Salesforce arrivals are unsigned and identified by this prefix on the query string.
export const LEGACY_PARAM_PREFIX = 'book_';

// The provider name book-api registers for that legacy arrival
// (services/crm/providers/salesforceijm).
export const LEGACY_PROVIDER = 'salesforce_ijm';

// book-api bounds a posted query at crm.MaxLinkParams; posting more is answered with
// `too_many_parameters`, so the SPA stops before making a request that cannot succeed.
export const MAX_LINK_PARAMS = 64;

// legacyLinkParams collects the `book_*` parameters of a legacy Salesforce URL.
//
// A repeated parameter arrives from react-router v3 as an array. It is dropped rather than
// resolved: `?book_email=a&book_email=b` has no correct reading, and picking one would pre-fill a
// booking with a buyer's address that the link did not unambiguously name. Empty values are
// dropped too — they carry nothing and only count against the parameter budget.
export function legacyLinkParams(location) {
  const query = (location && location.query) || {};
  const params = {};
  Object.keys(query).forEach((key) => {
    if (key.indexOf(LEGACY_PARAM_PREFIX) !== 0) return;
    const value = query[key];
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    params[key] = trimmed;
  });
  return params;
}

// queryWithoutLegacyParams is the same query with every `book_*` parameter removed.
//
// Once the parameters have been exchanged for a session id there is no reason for the buyer's
// name, mobile and email to stay in the address bar, where they are read by history, by the
// referrer on any outbound link, and by anyone looking at the tablet. Salesforce generates that
// URL and cannot be changed, but how long the SPA leaves it standing is ours to decide.
export function queryWithoutLegacyParams(query) {
  const next = {};
  Object.keys(query || {}).forEach((key) => {
    if (key.indexOf(LEGACY_PARAM_PREFIX) !== 0) next[key] = query[key];
  });
  return next;
}

// arrivalKey identifies which arrival a form is showing, so a second CRM link landing in the same
// tab can be told from the first. React Router reuses the booking form when only the query
// changes, so without this the second buyer's booking keeps the first buyer's session.
//
// Values are percent-encoded before joining. A raw join on `=` and `&` lets different parameter
// sets collapse to the same string — a value containing an encoded `&` reads back as an extra
// parameter — and two arrivals that compare equal would skip the restart.
export function arrivalKey(routeID, location) {
  if (routeID) return `id:${routeID}`;
  const params = legacyLinkParams(location);
  const names = Object.keys(params).sort();
  if (!names.length) return '';
  const pair = n => `${encodeURIComponent(n)}=${encodeURIComponent(params[n])}`;
  return `legacy:${names.map(pair).join('&')}`;
}

export function hasLegacyLinkParams(location) {
  return Object.keys(legacyLinkParams(location)).length > 0;
}

// splitPhone turns the prefill's E.164 number back into the pair the form holds.
//
// `prefill.buyer.phone1` is E.164 by contract (`+60123456789`); the form has always kept a
// dialling code and a national number in two fields. Splitting them needs to know which dialling
// codes exist, because "+60123..." could be +6 or +60 or +601 — so the caller passes the codes the
// app already loaded (fetchPhoneValidationPresets) and the LONGEST match wins, which is how
// dialling plans disambiguate.
//
// With no codes to match against, the number is left whole in `mobile` with no prefix. That is the
// honest outcome: a wrong split would silently produce an unreachable number, and a salesperson
// looking at a full +60... in one box can see what happened and fix it.
export function splitPhone(phone1, diallingCodes = []) {
  const raw = typeof phone1 === 'string' ? phone1.trim() : '';
  if (!raw || raw.charAt(0) !== '+') return { mobilePrefix: '', mobile: raw };
  const digits = raw.slice(1);
  if (!/^\d+$/.test(digits)) return { mobilePrefix: '', mobile: raw };

  const codes = (diallingCodes || [])
    .map(c => String(c === null || c === undefined ? '' : c).replace(/^\+/, '').trim())
    .filter(c => /^\d+$/.test(c))
    .sort((a, b) => b.length - a.length); // longest first: +60 must beat +6

  for (let i = 0; i < codes.length; i += 1) {
    const code = codes[i];
    const national = digits.indexOf(code) === 0 ? digits.slice(code.length) : '';
    // What is left has to be a number somebody could dial. A code that consumes everything leaves
    // nothing, and a remainder of zeros is not a number either — book-api's own e164 helper
    // refuses the same case. Splitting "+60" into "+6" and "0" would look like a phone number in
    // the form and reach nobody.
    if (national && /[1-9]/.test(national)) {
      return { mobilePrefix: code, mobile: national };
    }
  }
  return { mobilePrefix: '', mobile: raw };
}

// A legacy Salesforce session carries the untouched Salesforce record in
// `context.legacy_autofill`, which still has the dialling code and the national number in separate
// fields. When it is there it is better than splitting E.164 back apart, because it is what
// Salesforce actually said.
function phoneFromSession(session, diallingCodes) {
  const context = (session && session.context) || {};
  const record = context.legacy_autofill;
  if (record && typeof record === 'object') {
    const prefix = String(record.mobilePrefix || '').replace(/^\+/, '').trim();
    const mobile = String(record.mobile || '').trim();
    if (mobile) return { mobilePrefix: prefix, mobile };
  }
  const buyer = (session && session.prefill && session.prefill.buyer) || {};
  return splitPhone(buyer.phone1, diallingCodes);
}

// The legacy ids the form has always submitted back. They live in `context` rather than `prefill`
// because they are not the buyer: they are the references the IJM sync matches on, and losing them
// would break that sync. They are never rendered and never editable (T6.2).
export const LEGACY_CONTEXT_FIELDS = {
  book_external_id: 'externalID',
  book_external_lead_id: 'externalLeadID',
  book_external_customer_id: 'externalCustomerID',
};

// sourceCategoryOf is the CRM's IFCA Opportunity Source — the OP link's source_category (MS-6744)
// — or ''. Unlike lead_source it is not only displayed: it pre-fills Sales Source, which the form
// then shows locked; book-api validated it against the tenant's list when the session was created
// and writes it to bookings.sales_source at attach.
export function sourceCategoryOf(session) {
  const prefill = (session && session.prefill) || {};
  return String(prefill.source_category || '').trim();
}

// prefillToBooking maps a link session onto the fields the booking form holds.
//
// It returns only the keys it actually has a value for, so a caller merges rather than assigns:
// a session that names no email must not blank an email the salesperson already typed.
export function prefillToBooking(session, diallingCodes = []) {
  const prefill = (session && session.prefill) || {};
  const buyer = prefill.buyer || {};
  const context = (session && session.context) || {};
  const booking = {};

  const name = String(buyer.name || '').trim();
  if (name) booking.name = name;
  const email = String(buyer.email || '').trim();
  if (email) booking.email = email;

  // The CRM's IFCA Opportunity Source (MS-6744) becomes the booking's Sales Source: the form
  // shows it locked, and book-api writes the same value from the session at attach whatever is
  // posted, so the two can never disagree.
  const sourceCategory = sourceCategoryOf(session);
  if (sourceCategory) booking.salesSource = sourceCategory;

  const { mobilePrefix, mobile } = phoneFromSession(session, diallingCodes);
  if (mobile) {
    booking.mobile = mobile;
    // The prefix is always written alongside the number, INCLUDING when it is empty. The form
    // arrives with the company's default already in that box ('60' for a Malaysian tenant), so
    // leaving it there next to a number we could not split gives `+441234567890` a Malaysian
    // dialling code — a number that looks complete, fails the form's own validation, and reaches
    // nobody. An empty prefix next to a full international number is visibly incomplete, which is
    // the outcome a salesperson can actually act on.
    booking.mobilePrefix = mobilePrefix;
  }

  Object.keys(LEGACY_CONTEXT_FIELDS).forEach((key) => {
    const value = context[key];
    if (typeof value === 'string' && value.trim()) {
      booking[LEGACY_CONTEXT_FIELDS[key]] = value.trim();
    }
  });

  return booking;
}

// The Salesforce record's fields, mapped onto the booking form's.
//
// This is the mapping `getSFAutofill` used to apply after calling `/bookings/sf/autofill/` from the
// browser. book-api now makes that call itself while creating the session (MS-6622) and hands the
// cleaned record back in `context.legacy_autofill`, so the same fields are applied from data the
// SPA already has. Moving it here is what makes it testable: it was forty lines of assignments
// inside a 4,000-line component, and the IJM autofill is the flow the acceptance criteria protect.
//
// Left-hand side is the Salesforce field, right-hand side the booking field.
const LEGACY_AUTOFILL_FIELDS = {
  bumi: 'bumi',
  email: 'email',
  gender: 'gender',
  identity: 'identity',
  maritalStatus: 'maritalStatus',
  mobile: 'mobile',
  mobilePrefix: 'mobilePrefix',
  name: 'name',
  nationality: 'nationality',
  phone: 'phone',
  phonePrefix: 'phonePrefix',
  preferredName: 'preferredName',
  race: 'race',
  title: 'title',
  address1: 'address1',
  address2: 'address2',
  city: 'address6',
  postcode: 'address5',
  state: 'address7',
  country: 'country',
  birthdate: 'birthdate',
  emergencyContactRelation: 'emergencyContactRelation',
  emergencyContactName: 'emergencyContactName',
  emergencyContactMobilePrefix: 'emergencyContactMobilePrefix',
  emergencyContactMobile: 'emergencyContactMobile',
  emergencyContactIdentity: 'emergencyContactIdentity',
  emergencyContactEmail: 'emergencyContactEmail',
};

// legacyAutofillToBooking turns the Salesforce record into booking fields.
//
// Only truthy values are copied, exactly as the old code did: a blank in Salesforce must not wipe
// something the salesperson typed. `identityType` and `monthlyIncome` need converting, and
// `occupation` wins over `jobTitle` when both are present — all three were special cases in the
// original and stay special cases here rather than being tidied into something that behaves
// differently on a live IJM tenant.
export function legacyAutofillToBooking(record) {
  if (!record || typeof record !== 'object') return {};
  const booking = {};
  Object.keys(LEGACY_AUTOFILL_FIELDS).forEach((from) => {
    const value = record[from];
    if (value) booking[LEGACY_AUTOFILL_FIELDS[from]] = value;
  });
  if (record.identityType) {
    const typeID = parseInt(record.identityType, 10);
    if (!isNaN(typeID)) booking.identityTypeID = typeID;
  }
  if (record.monthlyIncome) {
    booking.monthlyIncome = isNaN(record.monthlyIncome)
      ? record.monthlyIncome : String(record.monthlyIncome);
  }
  // occupation wins: the original checked it first and fell back to jobTitle.
  if (record.occupation) booking.jobTitle = record.occupation;
  else if (record.jobTitle) booking.jobTitle = record.jobTitle;
  return booking;
}

// legacyAutofillOf pulls that record out of a session, when the session has one.
export function legacyAutofillOf(session) {
  const context = (session && session.context) || {};
  const record = context.legacy_autofill;
  return record && typeof record === 'object' ? record : null;
}

// legacyIdentityFrom reads the identity the legacy URL carried.
//
// `book_customer_identity`, `book_customer_identity_type` and `book_customer_id` are in the
// session's context, not in `prefill`: they are not the buyer's contact details but how the old
// flow recognised a customer MHub already had. Keeping them matters most in the case where the
// Salesforce lookup FAILED (`legacy_autofill_error`) — the URL's own values are then all that is
// left, and dropping them would make a returning buyer look new and be entered twice.
export function legacyIdentityFrom(session) {
  const context = (session && session.context) || {};
  const str = (k) => {
    const v = context[k];
    return typeof v === 'string' ? v.trim() : '';
  };
  const typeID = parseInt(str('book_customer_identity_type'), 10);
  return {
    identity: str('book_customer_identity'),
    identityTypeID: isNaN(typeID) ? 0 : typeID,
    customerID: str('book_customer_id'),
  };
}

// Every booking field a CRM arrival is allowed to write.
//
// This is the union of what prefillToBooking and legacyAutofillToBooking can produce, and it
// exists so that a SECOND arrival can wipe the first one's before filling in its own. Without
// that, a field buyer B's link does not mention keeps buyer A's value — B's booking submitted
// with A's email — because the pre-fill only ever overlays the fields it has.
export const CRM_OWNED_FIELDS = [
  'name', 'email', 'mobile', 'mobilePrefix',
  'identity', 'identityTypeID',
  'externalID', 'externalLeadID', 'externalCustomerID',
].concat(Object.keys(LEGACY_AUTOFILL_FIELDS).map(k => LEGACY_AUTOFILL_FIELDS[k]))
  .concat(['monthlyIncome', 'jobTitle'])
  // MS-6744: the CRM's IFCA category pre-fills Sales Source, so a later arrival must not inherit it
  .concat(['salesSource'])
  .filter((v, i, a) => a.indexOf(v) === i);

// clearedCRMFields resets every one of those to the form's own default, so a new arrival starts
// from a blank buyer rather than from the previous one.
export function clearedCRMFields(defaults = {}) {
  const cleared = {};
  CRM_OWNED_FIELDS.forEach((field) => {
    cleared[field] = Object.prototype.hasOwnProperty.call(defaults, field) ? defaults[field] : '';
  });
  return cleared;
}

// Which form fields a tag can appear on. `unit` is here because the link may name one, but it is
// not a text input — the unit is chosen before the form opens (MS-6620's destinationFor), so its
// provenance is recorded for the banner and never applied as a value.
export const PREFILLABLE_FIELDS = ['name', 'mobile', 'email', 'unit', 'crmSource', 'salesSource'];

// crmSourceOf is what the CRM said about where the lead came from — the OP link's lead_source —
// or ''. It is shown on the form read-only (MS-6628): book-api writes bookings.crm_source from the
// session when the booking is created, and discards anything a client posts for it, so the form
// only displays it and never sends it.
export function crmSourceOf(session) {
  const prefill = (session && session.prefill) || {};
  return String(prefill.lead_source || '').trim();
}

// sessionProvider is the provider name as book-api reported it — never a display string decided
// here, and never a branch on which CRM it is. The prefill's own says it first; the session's is
// the fallback. Empty when the session is missing or names no provider.
export function sessionProvider(session) {
  const prefill = (session && session.prefill) || {};
  return String(prefill.provider || (session && session.provider) || '').trim();
}

// sessionVerified reads whether the link this session came from was verified — a signed
// OutPerform link, as opposed to an unsigned legacy Salesforce URL, which is exactly the
// distinction an agent could not previously see.
//
// `prefill.verified` when it is PRESENT, even when it is false — NOT `a || b`, which reads an
// explicit false as "missing" and would fall through to the session's flag. Getting this wrong
// shows "verified" against a legacy Salesforce URL, and telling a signed link from an unsigned one
// is the entire point of the tag (MS-6626, MS-6627). Absent on both: unverified, the safe reading.
export function sessionVerified(session) {
  const prefill = (session && session.prefill) || {};
  return Boolean(
    prefill.verified === undefined || prefill.verified === null
      ? (session && session.verified) : prefill.verified,
  );
}

// prefillProvenance records where each pre-filled value came from, for the per-field tags in T6.2.
//
// `source` is the provider name as book-api reported it — never a display string decided here, and
// never a branch on which CRM it is. `verified` distinguishes a signed OutPerform link from an
// unsigned legacy Salesforce URL, which is exactly the distinction an agent could not previously
// see. `locked` is false for every buyer field: name, mobile and email are the buyer's details and
// a salesperson correcting a typo in front of the customer must not be blocked by the CRM having
// been wrong. The one locked field is `crmSource` (MS-6628): the CRM's own statement of where the
// lead came from, which the server writes and a client cannot change.
export function prefillProvenance(session, booking = {}) {
  const prefill = (session && session.prefill) || {};
  const provider = sessionProvider(session);
  if (!provider) return {};
  const verified = sessionVerified(session);
  const fields = {};
  const mark = (field) => {
    fields[field] = { source: provider, verified, locked: false };
  };

  if (booking.name) mark('name');
  if (booking.email) mark('email');
  if (booking.mobile) mark('mobile');
  // The unit's tag says "the CRM named this unit". After a swap and a refresh the session still
  // names the OLD unit while the route carries the new one, so when the caller says which unit is
  // on the page (booking.projectUnitID) the tag is only marked if it is the link's unit.
  const linkUnit = prefill.unit && prefill.unit.unit_xid;
  const onPage = booking.projectUnitID; // undefined, null or '' all mean "not saying"
  if (linkUnit && (!onPage || onPage === linkUnit)) mark('unit');
  // The CRM's own source is the one field that IS locked: it is the CRM's statement, not the
  // buyer's detail; the server derives it from the link session and ignores anything a client
  // posts for it (MS-6628), so the form shows it and never sends it.
  if (crmSourceOf(session)) fields.crmSource = { source: provider, verified, locked: true };
  // Sales Source is locked too when the CRM sent its IFCA category (MS-6744) AND that is the value
  // on the booking: the server writes that value at attach, so an edit here would only be
  // overwritten. The lock follows the applied value, never a category the form did not take.
  const sourceCategory = sourceCategoryOf(session);
  if (sourceCategory && booking.salesSource === sourceCategory) {
    fields.salesSource = { source: provider, verified, locked: true };
  }
  return fields;
}

// Fields that only mean anything together. A dialling code is not a fact about a booking on its
// own — it qualifies the number beside it — so a salesperson who typed a mobile has, in effect,
// spoken for its prefix too. Applying a CRM prefix to a number they entered produces a value
// neither of them chose: their digits under a different country's code.
export const FIELD_PAIRS = [
  ['mobile', 'mobilePrefix'],
  ['phone', 'phonePrefix'],
  ['emergencyContactMobile', 'emergencyContactMobilePrefix'],
  ['identity', 'identityTypeID'],
];

// withPairedFields expands a set of edited field names to include each one's partner.
export function withPairedFields(edited) {
  const next = Object.assign({}, edited || {});
  FIELD_PAIRS.forEach(([a, b]) => {
    if (next[a] || next[b]) {
      next[a] = true;
      next[b] = true;
    }
  });
  return next;
}

// dropProvenance removes a field's tag once the salesperson edits it.
//
// The tag says "this came from OutPerform". The moment somebody types over it that stops being
// true, and a tag that lies about where a value came from is worse than no tag: it is the thing an
// agent would rely on when deciding whether to double-check a number with the customer.
//
// A paired field goes with its partner (FIELD_PAIRS): changing the dialling code under a CRM's
// number leaves a number the CRM never sent, so the mobile's tag goes too; and editing the number
// drops the dialling code's tag, if it ever had one (MS-6627).
//
// Returns the same object when there is nothing to drop, so a caller can skip a re-render.
export function dropProvenance(fields, name) {
  if (!fields || !name) return fields || {};
  const names = [name];
  FIELD_PAIRS.forEach(([a, b]) => {
    if (name === a) names.push(b);
    if (name === b) names.push(a);
  });
  if (!names.some(n => fields[n])) return fields;
  const next = Object.assign({}, fields);
  names.forEach((n) => { delete next[n]; });
  return next;
}

// BUYER_FIELDS are the pre-fillable fields that describe the buyer; the unit is the other one.
export const BUYER_FIELDS = ['name', 'mobile', 'email'];

// dropBuyerProvenance removes every buyer tag at once, keeping the unit's: choosing an existing
// customer from the autofill list, or clearing the form, replaces who the booking is for, and a
// tag left beside a value the CRM never sent would be a lie about that person (MS-6627).
export function dropBuyerProvenance(fields) {
  return BUYER_FIELDS.reduce((acc, name) => dropProvenance(acc, name), fields || {});
}

// Reading a session can fail in ways the form must survive. It has a unit chosen and possibly a
// salesperson halfway through typing; losing the pre-fill is a nuisance, losing the booking is
// not acceptable. So a failed read is reported and the form stays usable.
//
// Final means "this session will never work again", so stop pointing the tab at it: `consumed`
// has already produced a booking, `not_found` never named one, and `other_user` belongs to a
// colleague. `expired` and `unavailable` are not final — the first can be reissued from the CRM
// and the second is usually a blip.
export function prefillFailureIsFinal(reason) {
  return reason === 'consumed' || reason === 'not_found' || reason === 'other_user';
}



// WEBPACK FOOTER //
// ./src/utils/crmPrefill.js